import React, {
  memo,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import moment from "moment";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import PopupTest from "./PopUpTest";
import MonthCalendarGrid, { buildMonthDays } from "./MonthCalendarGrid";
import MembersSortControl, { compareMembersBy } from "./MembersSortControl";
import { useAlert } from "../../../context/AlertContext";
import {
  useMembers,
  refreshMembers,
  upsertClientInCache,
  getMembersState,
  refreshClientPackages,
} from "../../../data/membersStore";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import {
  MORNING_HOURS,
  AFTERNOON_HOURS,
  ABSOLUTE_CAPACITY,
} from "../../../../common/utils/capacity";
import "./newAppointment.css";

// Search matches by keystroke are local (already-cached client array), so
// this debounce isn't masking network latency — it exists so typing fast
// doesn't re-filter + re-render the whole (~hundreds of members) list on
// every single keystroke, only once typing pauses.
const SEARCH_DEBOUNCE_MS = 300;

const memberAvatarSx = (gender) => ({
  width: 32,
  height: 32,
  fontSize: "12px",
  fontWeight: 600,
  color: "black",
  backgroundColor: gender === "Male" ? "#97dfff" : "#FF8E8B",
});

// Shared, memoized row used by both the desktop dropdown and the mobile
// step-1 list — extracted so typing in search (which re-renders the parent
// on every keystroke) doesn't re-render every row, and so the two
// previously-duplicated inline implementations stay in sync.
const MemberResultRow = memo(function MemberResultRow({
  client,
  pkg,
  isSelected,
  onSelect,
}) {
  return (
    <div
      className={`hf-naf-member-row${isSelected ? " selected" : ""}`}
      // Keeps the search input focused through the tap (blur fires on mousedown/touchstart
      // for the previously-focused element, before this row's own onClick runs) — without this,
      // tapping a result while the Mobile keyboard is still open would dismiss the keyboard and
      // snap the popup back to its normal (non keyboard-open) size/position mid-tap, right as
      // the finger is still on the screen.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onSelect(client)}
    >
      <Avatar sx={memberAvatarSx(client.gender)}>
        {client.first_name ? client.first_name[0].toUpperCase() : "?"}
        {client.last_name ? client.last_name[0].toUpperCase() : "?"}
      </Avatar>
      <div className="hf-naf-member-info">
        <span className="hf-naf-member-name">
          {client.first_name} {client.last_name}
        </span>
        <span className="hf-naf-member-contact">
          {client.email || client.phone || ""}
        </span>
      </div>
      {pkg && <span className="hf-naf-member-package">{pkg.package_name}</span>}
    </div>
  );
});

const MemberRowSkeleton = () => (
  <div className="hf-naf-member-row hf-naf-member-row-skeleton" aria-hidden="true">
    <div className="hf-naf-skel hf-naf-skel-avatar" />
    <div className="hf-naf-member-info">
      <div className="hf-naf-skel hf-naf-skel-line" style={{ width: "50%" }} />
      <div
        className="hf-naf-skel hf-naf-skel-line"
        style={{ width: "70%", marginTop: 4 }}
      />
    </div>
  </div>
);

const CANONICAL_HOURS = [...MORNING_HOURS, ...AFTERNOON_HOURS];

const STEP_TITLES = {
  1: "Izaberite člana",
  2: "Izaberite datum",
  3: "Izaberite termin",
  4: "Potvrda",
};

// Tablet/Desktop gets the single-page form (matching the reference design);
// Mobile keeps the existing 4-step wizard untouched. Tracked reactively so
// resizing the window (or rotating a tablet) switches layouts live.
const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

const NewAppointmentFlow = ({
  onClose,
  initialDate,
  initialHour,
  refresh,
  editingClient,
  editingAppointment,
  // Bulk-scheduling experiment (this branch only): strips this flow down to
  // just the member-search step — no Datum/Vreme/Save here at all. Picking a
  // member calls `onMemberChosen` instead of saving anything; the calendar
  // itself becomes the slot picker afterwards (see SchedulerTest.jsx).
  bulkMode = false,
  onMemberChosen,
}) => {
  const { showAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  // This whole flow (Select Member → New Member, all 4 steps) is one mount-gated overlay —
  // the page behind it shouldn't scroll while it's open.
  useLockBodyScroll();

  // Edit mode: opened from Calendar → Zakazani članovi → Izmeni termin, with
  // the booking already in place. Reuses this exact same component/markup —
  // only the initial field values, title/button copy, and save behavior
  // (move existing booking vs. create a new one) differ.
  const isEditMode = !!editingAppointment;
  const originalHour = editingAppointment
    ? moment(editingAppointment.start).hour()
    : null;
  const originalDateStr = editingAppointment
    ? moment(editingAppointment.start).format("YYYY-MM-DD")
    : null;

  // Edit mode on Mobile: open straight on the Datum step (2), not the empty step-1 member
  // search — the member is already fixed (this is *their* booking, entered from Booked
  // Members), so there's nothing to pick there. Also skips the confirm/summary step 4 as a
  // starting point — the admin came here specifically to change something, so land them
  // straight on something editable instead of a screen whose only useful action is going back
  // anyway. The back arrow still reaches step 3 (Vreme) to change the time, but is disabled at
  // step 2 — see the header's back-button condition below — since going back further to step 1
  // would let the member be changed, which doesn't make sense for editing an existing booking.
  const [step, setStep] = useState(() => (editingAppointment ? 2 : 1));

  // Shared cache — reused instantly if the Members screen (or a previous
  // open of this same popup) already loaded it and it's still fresh; only
  // hits the network on a genuine cold start or once the cache goes stale.
  const { clients, packagesByClient, loading: loadingClients } = useMembers();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Same options/control as the Members page (MembersSortControl) — Desktop/Tablet's "Izaberi
  // člana" section shows the control next to its search field; Mobile shares this same state
  // and comparator but has no control of its own, so it just always uses the default (Ime,
  // A → Š), same as before this was made adjustable.
  const [sortField, setSortField] = useState("first_name");
  const [sortDir, setSortDir] = useState("asc");
  // Mobile only: whether the search field is focused (keyboard open). Driving the wizard's
  // shrunk-and-repositioned state off plain focus/blur instead of any viewport-resize API —
  // window.visualViewport's resize event does not reliably fire on keyboard open in Chrome for
  // iOS (a known WKWebView limitation), so neither that nor the interactive-widget viewport meta
  // flag were enough there. Focus/blur is a plain DOM event every browser fires reliably.
  const [searchFocused, setSearchFocused] = useState(false);
  // Blurring the search field to tap something else *in this same step* (a member row, "Dodaj
  // novog člana", or the footer "Sačuvaj"/"Dalje" button) must not un-shrink the modal
  // immediately — see handleSearchBlur below for why.
  const searchBlurTimeoutRef = useRef(null);
  const handleSearchFocus = () => {
    if (searchBlurTimeoutRef.current) {
      clearTimeout(searchBlurTimeoutRef.current);
      searchBlurTimeoutRef.current = null;
    }
    setSearchFocused(true);
  };
  // Delayed, not immediate like the focus side above: a tap that blurs this field to activate
  // another control on this same step needs that control's own tap to actually land first. An
  // immediate `setSearchFocused(false)` here snaps `.hf-naf-kb-open` (newAppointment.css) off
  // instantly, and that CSS swings the whole modal between two entirely different layouts (a
  // small one anchored to the top vs. a tall one anchored to the bottom) — happening mid-tap, it
  // moves whatever the finger was about to land on out from under it before the tap completes,
  // so the tap lands on nothing. Confirmed exactly this way: after searching for and tapping an
  // existing member (MemberResultRow's own onMouseDown already keeps the field focused through
  // that specific tap — see its comment — so this was never about the row itself), tapping the
  // footer "Sačuvaj" right after did blur the field and silently ate that tap. The same bug
  // already fixed once for the member-details popup's X button (memberPopup.css) and its gender
  // chips — this is the same family, just this popup's own search field. Cleared by
  // handleSearchFocus above if focus lands on the search field again within the delay (e.g.
  // typing resumes), so moving focus between fields still doesn't flicker the modal.
  const handleSearchBlur = () => {
    searchBlurTimeoutRef.current = setTimeout(() => {
      setSearchFocused(false);
      searchBlurTimeoutRef.current = null;
    }, 250);
  };
  useEffect(() => {
    return () => {
      if (searchBlurTimeoutRef.current) clearTimeout(searchBlurTimeoutRef.current);
    };
  }, []);
  const [selectedMember, setSelectedMember] = useState(
    () => editingClient || null
  );
  const [showAddMember, setShowAddMember] = useState(false);

  const [viewMonth, setViewMonth] = useState(
    moment(
      (editingAppointment ? editingAppointment.start : null) ||
        initialDate ||
        new Date()
    ).startOf("month")
  );
  // A tapped empty cell in the Mobile Week View (SchedulerTest.jsx) passes both initialDate and
  // initialHour together to preselect that exact slot — unlike the Desktop/Tablet-only
  // initialDate-seeds-selectedDate effect below, this applies on Mobile too, since it reflects
  // an explicit user pick (a specific cell tap), not just "the week currently being viewed".
  const [selectedDate, setSelectedDate] = useState(() => {
    if (editingAppointment) return moment(editingAppointment.start);
    if (initialHour != null && initialDate) return moment(initialDate);
    return null;
  });

  const [timeslotMap, setTimeslotMap] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedHour, setSelectedHour] = useState(() => {
    if (editingAppointment) return moment(editingAppointment.start).hour();
    return initialHour ?? null;
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Desktop/tablet-only: which field popover (if any) is open.
  const [openPopover, setOpenPopover] = useState(null); // "date" | "time" | null
  // Whether the open popover renders above or below its field — flips to
  // "up" when there isn't enough room below within the viewport (the field
  // sits near the bottom of the modal), so the calendar/hour grid never
  // gets clipped off-screen.
  const [popoverDirection, setPopoverDirection] = useState("down");
  const dateFieldRef = useRef(null);
  const timeFieldRef = useRef(null);
  const popoverRef = useRef(null);
  const modalRef = useRef(null);

  useLayoutEffect(() => {
    if (!openPopover) return;
    const fieldEl =
      (openPopover === "date" ? dateFieldRef : timeFieldRef).current;
    const popoverEl = popoverRef.current;
    if (!fieldEl || !popoverEl) return;
    const fieldRect = fieldEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - fieldRect.bottom;
    const spaceAbove = fieldRect.top;
    const popoverHeight = popoverEl.offsetHeight;
    setPopoverDirection(
      spaceBelow < popoverHeight + 16 && spaceAbove > spaceBelow
        ? "up"
        : "down"
    );
  }, [openPopover]);

  // Debounce the value that actually drives filtering — the input itself
  // stays bound to `search` so typing never feels delayed, only the
  // (potentially large) list re-filter + re-render is deferred.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Edit mode initially seeds `selectedMember` from the lightweight booking
  // row (client_id/first_name/last_name/gender only, no email/phone). Once
  // the full client list has loaded, upgrade it to the matching full record
  // so the selected-member card shows the same detail it would in Add mode.
  useEffect(() => {
    if (!isEditMode || loadingClients) return;
    const originalId = editingClient.client_id ?? editingClient.id;
    const full = clients.find((c) => c.id === originalId);
    if (full) setSelectedMember(full);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingClients]);

  // Dismiss a stale "slot is now full" error as soon as the user changes
  // any part of the selection.
  useEffect(() => {
    setSaveError(null);
  }, [selectedMember, selectedDate, selectedHour]);

  // Desktop/tablet only: default Datum to the calendar's currently-viewed
  // date, matching the reference (its date field is pre-filled, unlike
  // Vreme). Mobile's step wizard is untouched — it still requires an
  // explicit pick.
  useEffect(() => {
    if (isDesktopTablet && !selectedDate) {
      setSelectedDate(moment(initialDate || new Date()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktopTablet]);

  // Desktop/tablet only: Escape closes an open popover first, else the
  // whole modal.
  useEffect(() => {
    if (!isDesktopTablet) return;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (openPopover) {
        setOpenPopover(null);
      } else {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktopTablet, openPopover]);

  // Desktop/tablet only: clicking outside an open field popover closes it.
  useEffect(() => {
    if (!isDesktopTablet || !openPopover) return;
    const ref = openPopover === "date" ? dateFieldRef : timeFieldRef;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpenPopover(null);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isDesktopTablet, openPopover]);

  const filteredClients = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const pool = q
      ? clients.filter((c) => {
          const haystack = `${c.first_name || ""} ${c.last_name || ""} ${
            c.email || ""
          } ${c.phone || ""}`.toLowerCase();
          return haystack.includes(q);
        })
      : clients;
    return [...pool].sort(compareMembersBy(sortField, sortDir));
  }, [clients, debouncedSearch, sortField, sortDir]);

  const currentPackageFor = (clientId) => {
    const packages = packagesByClient[clientId] || [];
    if (!packages.length) return null;
    return [...packages].sort((a, b) =>
      moment(b.start_date).diff(moment(a.start_date))
    )[0];
  };

  // Called only on an actual successful save (PopUpTest's `openPopup(client)` after onSave) —
  // never on a plain cancel. The new client is added straight to the shared cache and selected
  // immediately — no full member-list refetch. Only falls back to a real refresh if an older
  // deployed backend hasn't started returning the created row yet (see PopUpTest.jsx's onSave).
  const handleMemberCreated = async (newClient) => {
    setShowAddMember(false);
    if (newClient) {
      upsertClientInCache(newClient);
      setSelectedMember(newClient);
      return;
    }
    try {
      await refreshMembers();
      const list = getMembersState().clients;
      if (list.length > 0) {
        const newest = [...list].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        )[0];
        setSelectedMember(newest);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Back arrow / X on the New Member layer — the admin didn't save anything, so this just
  // closes that layer and returns to the already-mounted Select Member step underneath, with
  // no refetch and no selection change. Search text, results, selected date/time, and the rest
  // of the appointment flow's state all live in this same component and were never touched by
  // opening/closing the child popup, so they're preserved automatically.
  const handleMemberCancel = () => {
    setShowAddMember(false);
  };

  const monthDays = useMemo(() => buildMonthDays(viewMonth), [viewMonth]);

  const today = moment().startOf("day");

  const isSelectableDay = (day) => {
    if (!day) return false;
    if (day.isBefore(today, "day")) return false;
    if (day.day() === 0) return false;
    return true;
  };

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = selectedDate.format("YYYY-MM-DD");
    // Edit mode: landing back on the appointment's original day should keep
    // (or restore) its original hour selected instead of clearing it — the
    // member is already booked there.
    const isOriginalDay = isEditMode && dateStr === originalDateStr;
    const fetchSlots = async () => {
      setLoadingSlots(true);
      setSelectedHour(isOriginalDay ? originalHour : null);
      try {
        const startOfWeek = selectedDate.clone().startOf("week");
        const response = await ApiMethods.get(
          `auth/bookings_per_client_timeslot?start=${startOfWeek.format(
            "YYYY-MM-DD"
          )}`
        );
        const map = {};
        (response || []).forEach((appointment) => {
          const start = moment.parseZone(appointment.start);
          if (start.format("YYYY-MM-DD") === dateStr) {
            map[start.hour()] = {
              id: appointment.id,
              start: start.format("YYYY-MM-DD HH:mm:ss"),
              max: appointment.max_bookings || 7,
              used: appointment.used_bookings || 0,
            };
          }
        });
        setTimeslotMap(map);
      } catch (err) {
        console.log(err);
        setTimeslotMap({});
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleSave = async () => {
    if (!selectedMember || !selectedDate || selectedHour == null) return;
    const slot = timeslotMap[selectedHour];
    if (!slot) return;

    const clientId =
      selectedMember.id != null ? selectedMember.id : selectedMember.client_id;

    if (isEditMode) {
      const originalClientId =
        editingClient.client_id != null
          ? editingClient.client_id
          : editingClient.id;
      const isSameSlot = slot.id === editingAppointment.id;
      const isSameClient = clientId === originalClientId;

      // Re-check capacity right before saving — the slot's used/max in
      // `timeslotMap` was fetched when the date was picked and may be stale
      // by the time Save is clicked. The member being edited is still
      // counted in their own original slot until this save lands, so don't
      // count them against themselves. Gated by ABSOLUTE_CAPACITY (7), not
      // slot.max (6 on a morning slot) — this is the Office App, which is
      // allowed to push a morning slot one member past its normal capacity.
      const effectiveUsed = isSameSlot ? Math.max(0, slot.used - 1) : slot.used;
      if (effectiveUsed >= ABSOLUTE_CAPACITY) {
        setSaveError(
          "Ovaj termin je sada popunjen. Izaberite drugi termin."
        );
        return;
      }

      setSaving(true);
      try {
        const timeSlots = [];
        if (!isSameSlot || !isSameClient) {
          timeSlots.push({
            client_id: originalClientId,
            status: "canceled",
            start: editingAppointment.start,
            timeSlot_id: editingAppointment.id,
          });
          timeSlots.push({
            client_id: clientId,
            status: "book",
            start: slot.start,
            timeSlot_id: slot.id,
          });
        }
        if (timeSlots.length > 0) {
          await ApiMethods.post("auth/add_booking_cancelation", {
            timeSlots,
          });
        }
        showAlert("Termin je uspešno izmenjen.", "success");
        // Package usage is computed live from cal_bookings (no counter in the mutation's own
        // response to read off), so refresh just the affected client(s)' cached package rows
        // instead of the whole member list — the Members screen's used/total badge and the
        // package popup's "Predstojeći termini" list both read from this same cache.
        refreshClientPackages(clientId);
        if (!isSameClient) refreshClientPackages(originalClientId);
        // Tell the caller whether this save actually moved the member to a
        // different slot/timeslot — SchedulerTest.jsx uses this to decide
        // whether the Booked Members popup for the *old* slot should close
        // outright (moved) or just resync itself (edited in place).
        if (refresh) refresh({ slotChanged: !isSameSlot || !isSameClient });
        onClose();
      } catch (err) {
        console.log(err);
        if (err?.code === "NO_AVAILABLE_PACKAGE") {
          showAlert(err.message, "error");
        } else {
          showAlert("Greška prilikom izmene termina", "error");
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      const data = {
        timeSlots: [
          {
            client_id: clientId,
            status: "book",
            start: slot.start,
            timeSlot_id: slot.id,
          },
        ],
      };
      await ApiMethods.post("auth/add_booking_cancelation", data);
      showAlert("Termin je uspešno zakazan.", "success");
      // See the edit branch above — refresh just this client's cached package/usage rows so
      // Members and the package popup reflect the new booking without a full member reload.
      refreshClientPackages(clientId);
      if (refresh) refresh();
      onClose();
    } catch (err) {
      console.log(err);
      if (err?.code === "NO_AVAILABLE_PACKAGE") {
        showAlert(err.message, "error");
      } else {
        showAlert("Greška prilikom zakazivanja termina", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    !!selectedMember && !!selectedDate && selectedHour != null && !saving;

  const monthPicker = (
    <MonthCalendarGrid
      viewMonth={viewMonth}
      onPrevMonth={() => setViewMonth((m) => m.clone().subtract(1, "month"))}
      onNextMonth={() => setViewMonth((m) => m.clone().add(1, "month"))}
      monthDays={monthDays}
      today={today}
      selectedDate={selectedDate}
      isSelectableDay={isSelectableDay}
      onSelectDay={(day) => {
        setSelectedDate(day);
        setOpenPopover(null);
      }}
    />
  );

  const hourPicker = (
    <>
      {loadingSlots && <div className="hf-naf-empty">Učitavanje...</div>}
      {!loadingSlots && (
        <div className="hf-naf-hour-grid">
          {CANONICAL_HOURS.map((hour) => {
            const slot = timeslotMap[hour];
            const max = slot?.max || 7;
            const used = slot?.used || 0;
            // The member being edited is already counted in `used` for
            // their own current slot — don't hold that against them when
            // deciding whether it's still selectable.
            const isOriginalSlot =
              isEditMode &&
              selectedDate &&
              originalDateStr &&
              selectedDate.format("YYYY-MM-DD") === originalDateStr &&
              hour === originalHour;
            const effectiveUsed = isOriginalSlot
              ? Math.max(0, used - 1)
              : used;
            // Gated by ABSOLUTE_CAPACITY (7), not `max` (6 on a morning slot) — the Office App
            // is allowed to push a morning slot one member past its normal capacity, so a 6/6
            // morning chip must stay clickable up to 7, only disabling once the hard ceiling is
            // reached. `max` is still used above/below purely for the displayed "{used}/{max}"
            // label, which is exactly where the normal (not absolute) capacity belongs.
            const isFull = !!slot && effectiveUsed >= ABSOLUTE_CAPACITY;
            const isAvailable = !!slot && !isFull;
            const isSelected = selectedHour === hour;
            return (
              <button
                type="button"
                key={hour}
                disabled={!isAvailable}
                className={`hf-naf-hour-chip${isSelected ? " selected" : ""}${
                  isFull ? " full" : ""
                }${!slot ? " unavailable" : ""}${
                  isOriginalSlot ? " current" : ""
                }`}
                onClick={() => {
                  setSelectedHour(hour);
                  setOpenPopover(null);
                }}
              >
                <span className="hf-naf-hour-label">{hour}:00</span>
                <span className="hf-naf-hour-capacity">
                  {slot ? `${used}/${max}` : "—"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // Cold load only — a background refresh of already-cached data doesn't
  // block the dropdown with skeletons, it just keeps showing what's there.
  const showMemberSkeleton = loadingClients && clients.length === 0;

  const selectMember = (client) => {
    setSelectedMember(client);
    setSearch("");
  };

  const memberResults = (
    <>
      {showMemberSkeleton &&
        Array.from({ length: 4 }).map((_, i) => <MemberRowSkeleton key={i} />)}
      {!showMemberSkeleton && filteredClients.length === 0 && (
        <div className="hf-naf-empty">Nema rezultata</div>
      )}
      {!showMemberSkeleton &&
        filteredClients.map((client) => (
          <MemberResultRow
            key={client.id}
            client={client}
            pkg={currentPackageFor(client.id)}
            isSelected={false}
            onSelect={selectMember}
          />
        ))}
    </>
  );

  // ================= Desktop/Tablet: single-page form =================
  const renderDesktopModal = () => (
    <div className="hf-naf-overlay">
      <div className="hf-naf-d-modal" ref={modalRef}>
        <div className="hf-naf-d-header">
          <h2>{isEditMode ? "Izmeni termin" : "Novi termin"}</h2>
          <button
            type="button"
            className="hf-naf-close"
            onClick={onClose}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="hf-naf-d-body">
          <div className="hf-naf-d-section">
            <label className="hf-naf-d-label">
              {bulkMode ? "Izaberi člana" : "Član"}
            </label>
            <div className="hf-naf-d-search-row">
              <div className="hf-naf-search">
                <SearchIcon sx={{ fontSize: 17 }} />
                <input
                  type="text"
                  placeholder="Ime, telefon ili email"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    if (selectedMember) setSelectedMember(null);
                  }}
                />
              </div>
              <MembersSortControl
                field={sortField}
                dir={sortDir}
                onSelect={(nextField, nextDir) => {
                  setSortField(nextField);
                  setSortDir(nextDir);
                }}
              />
            </div>

            {selectedMember && !search && (
              <div className="hf-naf-d-selected-member">
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "black",
                    backgroundColor:
                      selectedMember.gender === "Male" ? "#97dfff" : "#FF8E8B",
                  }}
                >
                  {selectedMember.first_name
                    ? selectedMember.first_name[0].toUpperCase()
                    : "?"}
                  {selectedMember.last_name
                    ? selectedMember.last_name[0].toUpperCase()
                    : "?"}
                </Avatar>
                <div className="hf-naf-member-info">
                  <span className="hf-naf-member-name">
                    {selectedMember.first_name} {selectedMember.last_name}
                  </span>
                  <span className="hf-naf-member-contact">
                    {selectedMember.email || selectedMember.phone || ""}
                  </span>
                </div>
                <button
                  type="button"
                  className="hf-naf-d-clear-member"
                  aria-label="Ukloni izabranog člana"
                  onClick={() => setSelectedMember(null)}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            )}

            {(!selectedMember || search) && (
              <div className="hf-naf-d-member-dropdown">{memberResults}</div>
            )}

            <button
              type="button"
              className="hf-naf-d-add-member"
              onClick={() => setShowAddMember(true)}
            >
              <PersonAddAlt1Icon sx={{ fontSize: 16 }} />
              Dodaj novog člana
            </button>
          </div>

          {!bulkMode && (
          <div className="hf-naf-d-row">
            <div className="hf-naf-d-field-wrap" ref={dateFieldRef}>
              <label className="hf-naf-d-label">Datum</label>
              <button
                type="button"
                className="hf-naf-d-field"
                onClick={() =>
                  setOpenPopover((p) => (p === "date" ? null : "date"))
                }
              >
                <span>
                  {selectedDate
                    ? selectedDate.format("ddd, MMM D")
                    : "Izaberi datum"}
                </span>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
              </button>
              {openPopover === "date" && (
                <div
                  ref={popoverRef}
                  className={`hf-naf-d-popover hf-naf-date-picker${
                    popoverDirection === "up" ? " hf-naf-popover-up" : ""
                  }`}
                >
                  {monthPicker}
                </div>
              )}
            </div>

            <div className="hf-naf-d-field-wrap" ref={timeFieldRef}>
              <label className="hf-naf-d-label">Vreme</label>
              <button
                type="button"
                className="hf-naf-d-field"
                disabled={!selectedDate}
                onClick={() =>
                  setOpenPopover((p) => (p === "time" ? null : "time"))
                }
              >
                <span>
                  {selectedHour != null ? `${selectedHour}:00` : "Izaberi vreme"}
                </span>
                <AccessTimeOutlinedIcon sx={{ fontSize: 17 }} />
              </button>
              {openPopover === "time" && (
                <div
                  ref={popoverRef}
                  className={`hf-naf-d-popover hf-naf-time-picker${
                    popoverDirection === "up" ? " hf-naf-popover-up" : ""
                  }`}
                >
                  {hourPicker}
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {!bulkMode && saveError && (
          <div className="hf-naf-save-error">{saveError}</div>
        )}

        <div className="hf-naf-d-footer">
          <button type="button" className="hf-naf-d-cancel" onClick={onClose}>
            Otkaži
          </button>
          {bulkMode ? (
            <button
              type="button"
              className="hf-naf-save-btn"
              disabled={!selectedMember}
              onClick={() => onMemberChosen(selectedMember)}
            >
              Sačuvaj
            </button>
          ) : (
            <button
              type="button"
              className={`hf-naf-save-btn${saving ? " hf-loading" : ""}`}
              disabled={!canSave}
              onClick={handleSave}
            >
              {saving && <span className="hf-btn-spinner" />}
              {isEditMode ? "Sačuvaj izmene" : "Sačuvaj termin"}
            </button>
          )}
        </div>
      </div>

      {showAddMember && (
        <PopupTest
          activeClientInformation={null}
          openPopup={handleMemberCreated}
          onCancel={handleMemberCancel}
          refresh={() => {}}
          setActiveClientInformation={() => {}}
        />
      )}
    </div>
  );

  // ================= Mobile: existing 4-step wizard (untouched) =================
  const renderMobileWizard = () => (
    <div className="hf-naf-overlay">
      <div
        className={`hf-naf-modal${
          step === 1 && searchFocused ? " hf-naf-kb-open" : ""
        }`}
      >
        <div className="hf-naf-header">
          {/* Edit mode starts on step 2 (Datum) with the member already fixed — no back button
              there, since step 1 (Izaberite člana) is exactly the thing editing shouldn't let
              you change. Steps 3/4 still go back one step as usual (to Datum, then Vreme). */}
          {step > 1 && !(isEditMode && step === 2) ? (
            <button
              type="button"
              className="hf-naf-back"
              onClick={() => setStep((s) => s - 1)}
              aria-label="Nazad"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
            </button>
          ) : (
            <span className="hf-naf-back-spacer" />
          )}
          <span className="hf-naf-title">{STEP_TITLES[step]}</span>
          <button
            type="button"
            className="hf-naf-close"
            onClick={onClose}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        {!bulkMode && (
        <div className="hf-naf-steps">
          {[1, 2, 3, 4].map((s) => (
            <span
              key={s}
              className={`hf-naf-dot${step === s ? " active" : ""}${
                step > s ? " done" : ""
              }`}
            />
          ))}
        </div>
        )}

        {step === 1 && (
          <div className="hf-naf-search-bar">
            <div className="hf-naf-search">
              <SearchIcon sx={{ fontSize: 17 }} />
              <input
                type="text"
                placeholder="Ime, prezime, email ili telefon"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
              />
            </div>

            <button
              type="button"
              className="hf-naf-add-member"
              onClick={() => setShowAddMember(true)}
            >
              <PersonAddAlt1Icon sx={{ fontSize: 16 }} />
              Dodaj novog člana
            </button>
          </div>
        )}

        <div className="hf-naf-body">
          {step === 1 && (
            <div className="hf-naf-member-list">
              {showMemberSkeleton &&
                Array.from({ length: 5 }).map((_, i) => (
                  <MemberRowSkeleton key={i} />
                ))}
              {!showMemberSkeleton && filteredClients.length === 0 && (
                <div className="hf-naf-empty">Nema rezultata</div>
              )}
              {!showMemberSkeleton &&
                filteredClients.map((client) => (
                  <MemberResultRow
                    key={client.id}
                    client={client}
                    pkg={currentPackageFor(client.id)}
                    isSelected={selectedMember?.id === client.id}
                    onSelect={setSelectedMember}
                  />
                ))}
            </div>
          )}

          {step === 2 && <div className="hf-naf-date-picker">{monthPicker}</div>}

          {step === 3 && (
            <div className="hf-naf-time-picker">{hourPicker}</div>
          )}

          {step === 4 && (
            <div className="hf-naf-summary">
              <div className="hf-naf-summary-row">
                <span className="hf-naf-summary-label">Član</span>
                <span className="hf-naf-summary-value">
                  {selectedMember?.first_name} {selectedMember?.last_name}
                </span>
              </div>
              <div className="hf-naf-summary-row">
                <span className="hf-naf-summary-label">Datum</span>
                <span className="hf-naf-summary-value">
                  {selectedDate?.format("DD.MM.YYYY")}
                </span>
              </div>
              <div className="hf-naf-summary-row">
                <span className="hf-naf-summary-label">Vreme</span>
                <span className="hf-naf-summary-value">
                  {selectedHour}:00
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="hf-naf-footer">
          {bulkMode ? (
            <button
              type="button"
              className="hf-naf-next-btn"
              disabled={!selectedMember}
              onClick={() => onMemberChosen(selectedMember)}
            >
              Sačuvaj
            </button>
          ) : (
            step < 4 && (
              <button
                type="button"
                className="hf-naf-next-btn"
                disabled={
                  (step === 1 && !selectedMember) ||
                  (step === 2 && !selectedDate) ||
                  (step === 3 && selectedHour == null)
                }
                onClick={() => setStep((s) => s + 1)}
              >
                Dalje
              </button>
            )
          )}
          {step === 4 && (
            <>
              {saveError && (
                <div className="hf-naf-save-error">{saveError}</div>
              )}
              <button
                type="button"
                className={`hf-naf-save-btn${saving ? " hf-loading" : ""}`}
                disabled={!canSave}
                onClick={handleSave}
              >
                {saving && <span className="hf-btn-spinner" />}
                {isEditMode ? "Sačuvaj izmene" : "Sačuvaj termin"}
              </button>
            </>
          )}
        </div>
      </div>

      {showAddMember && (
        <PopupTest
          activeClientInformation={null}
          openPopup={handleMemberCreated}
          onCancel={handleMemberCancel}
          onBack={handleMemberCancel}
          refresh={() => {}}
          setActiveClientInformation={() => {}}
        />
      )}
    </div>
  );

  return isDesktopTablet ? renderDesktopModal() : renderMobileWizard();
};

export default NewAppointmentFlow;
